'use client';

import { getPrinterByUid, sendZpl } from './browserprint-client';
import {
  buildBatchLabels,
  buildGridZpl,
  type BuildLabelOptions,
  type GridLayoutOptions,
  type QrLabelInput
} from './zpl-builder';
import {
  matchProfile,
  profileToOptions,
  type PrinterProfile
} from './printer-profile';

export interface PrintLabelsArgs {
  printerUid: string;
  labels: QrLabelInput[];
  /**
   * Per-printer profiles managed in Configurations. The first profile
   * whose `name_pattern` matches the connected printer's name (case-
   * insensitive substring) supplies the ZPL options. Falls back to
   * `fallbackOptions` when no profile matches.
   */
  profiles?: PrinterProfile[];
  fallbackOptions: BuildLabelOptions;
}

export type PrintLabelsResult =
  | { ok: true; profileUsed: PrinterProfile | null }
  | {
      ok: false;
      error: string;
      reason: 'service-missing' | 'no-printer' | 'send-failed';
    };

/**
 * Send one or more QR labels to a Zebra printer via BrowserPrint.
 * Resolves dimensions+DPI from the matching printer profile, falling
 * back to the global label config when no profile matches. Never
 * throws — surfaces decide how to render the failure.
 */
export async function printLabels(
  args: PrintLabelsArgs
): Promise<PrintLabelsResult> {
  if (args.labels.length === 0) {
    return { ok: true, profileUsed: null };
  }

  let device;
  try {
    device = await getPrinterByUid(args.printerUid);
  } catch (err) {
    return {
      ok: false,
      reason: 'service-missing',
      error: err instanceof Error ? err.message : String(err)
    };
  }

  if (!device) {
    return {
      ok: false,
      reason: 'no-printer',
      error: `Printer ${args.printerUid} not found among local devices`
    };
  }

  const profile = matchProfile(device.name, args.profiles ?? []);
  // Profile supplies the printer-specific physical dimensions + DPI; the
  // QR↔text gaps and per-row font heights are global UX preferences and
  // must come from the caller's fallbackOptions even when a profile
  // matches.
  const options: BuildLabelOptions = profile
    ? {
        ...profileToOptions(profile),
        qrHeaderGapMm: args.fallbackOptions.qrHeaderGapMm,
        qrFooterGapMm: args.fallbackOptions.qrFooterGapMm,
        qrHeaderFontMm: args.fallbackOptions.qrHeaderFontMm,
        qrFooterFontMm: args.fallbackOptions.qrFooterFontMm
      }
    : args.fallbackOptions;
  const zpl = buildBatchLabels(args.labels, options);

  try {
    await sendZpl(device, zpl);
    return { ok: true, profileUsed: profile };
  } catch (err) {
    return {
      ok: false,
      reason: 'send-failed',
      error: err instanceof Error ? err.message : String(err)
    };
  }
}

export interface PrintGridLabelsArgs {
  printerUid: string;
  labels: QrLabelInput[];
  /** Paper + grid geometry. DPI here is the fallback only. */
  layout: GridLayoutOptions;
  /**
   * Optional per-printer profiles. When supplied, the matching profile's
   * DPI overrides `layout.dpi` so grid output prints at the printer's
   * native resolution (e.g. 600 dpi on a ZT610). Without this, grid mode
   * always rendered at the global fallback DPI — wrong for any non-203
   * printer.
   */
  profiles?: PrinterProfile[];
}

export type PrintGridLabelsResult =
  | { ok: true; sheets: number; profileUsed: PrinterProfile | null }
  | {
      ok: false;
      error: string;
      reason: 'service-missing' | 'no-printer' | 'send-failed' | 'bad-layout';
    };

/**
 * Send labels to a Zebra in grid/continuous mode: each `cols × rows` chunk
 * is one feed (one sheet) carrying multiple cells. Use this when printing
 * onto pre-cut sticker sheets OR onto continuous multi-column roll media.
 *
 * The DPI used for ZPL geometry is taken from the matched printer profile
 * when one is supplied; otherwise it falls back to `layout.dpi`.
 */
export async function printGridLabels(
  args: PrintGridLabelsArgs
): Promise<PrintGridLabelsResult> {
  const cellsPerSheet = Math.max(1, args.layout.cols * args.layout.rows);
  if (args.labels.length === 0) {
    return { ok: true, sheets: 0, profileUsed: null };
  }

  let device;
  try {
    device = await getPrinterByUid(args.printerUid);
  } catch (err) {
    return {
      ok: false,
      reason: 'service-missing',
      error: err instanceof Error ? err.message : String(err)
    };
  }

  if (!device) {
    return {
      ok: false,
      reason: 'no-printer',
      error: `Printer ${args.printerUid} not found among local devices`
    };
  }

  const profile = matchProfile(device.name, args.profiles ?? []);
  const layout: GridLayoutOptions = profile
    ? { ...args.layout, dpi: profile.dpi }
    : args.layout;

  const zpl = buildGridZpl(args.labels, layout);
  if (!zpl) {
    return {
      ok: false,
      reason: 'bad-layout',
      error: 'Grid geometry is invalid (cell width/height ≤ 0)'
    };
  }

  try {
    await sendZpl(device, zpl);
    return {
      ok: true,
      sheets: Math.ceil(args.labels.length / cellsPerSheet),
      profileUsed: profile
    };
  } catch (err) {
    return {
      ok: false,
      reason: 'send-failed',
      error: err instanceof Error ? err.message : String(err)
    };
  }
}
